import { useState, useCallback } from 'react';

const STORAGE_KEY = 'dextoro_recent_viewed';
const MAX_ITEMS = 5;

export interface RecentlyViewedToken {
  mint: string;
  name: string;
  symbol: string;
  imageUrl: string | null;
  timestamp: number;
}

function readViewed(): RecentlyViewedToken[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as RecentlyViewedToken[];
  } catch {
    return [];
  }
}

function writeViewed(items: RecentlyViewedToken[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Ignore write failures
  }
}

export function useRecentlyViewed() {
  const [viewedTokens, setViewedTokens] = useState<RecentlyViewedToken[]>(readViewed);

  const trackView = useCallback(
    (token: { mint: string; name: string; symbol: string; imageUrl: string | null }) => {
      setViewedTokens((prev) => {
        const filtered = prev.filter((v) => v.mint !== token.mint);
        const next = [
          { ...token, timestamp: Date.now() },
          ...filtered,
        ].slice(0, MAX_ITEMS);
        writeViewed(next);
        return next;
      });
    },
    []
  );

  const removeView = useCallback((mint: string) => {
    setViewedTokens((prev) => {
      const next = prev.filter((v) => v.mint !== mint);
      writeViewed(next);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    writeViewed([]);
    setViewedTokens([]);
  }, []);

  return { viewedTokens, trackView, removeView, clearAll };
}
